<template>
  <div class="app-header-shell">
    <header class="app-header" :class="{ 'is-scrolled': isScrolled, 'menu-open': mobileOpen }">
      <div class="container header-inner">
        <router-link to="/" class="logo" @click.native="closeMobile">
          <span class="logo-mark" aria-hidden="true">
            <img v-if="showLogoImage" :src="site.logoUrl" alt="" class="logo-img">
            <span v-else class="logo-icon">{{ site.logoEmoji }}</span>
          </span>
          <span class="logo-text">
            <strong class="logo-title">{{ site.name }}</strong>
            <em v-if="site.logoSubtitle" class="logo-sub">{{ site.logoSubtitle }}</em>
          </span>
        </router-link>

        <nav class="nav-menu desktop-nav" aria-label="主导航">
          <router-link
            v-for="(item, index) in navItems"
            :key="'d-' + index"
            :to="getNavTo(item)"
            class="nav-item"
            :class="{ 'is-active': isNavActive(item) }"
          >
            {{ item.label }}
          </router-link>
        </nav>

        <div class="header-search desktop-search">
          <el-input
            v-model="keyword"
            placeholder="搜索文章..."
            prefix-icon="el-icon-search"
            size="small"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </div>

        <div class="header-actions">
          <user-area />
          <button
            type="button"
            class="menu-toggle"
            :class="{ open: mobileOpen }"
            :aria-expanded="mobileOpen ? 'true' : 'false'"
            aria-label="打开菜单"
            @click="toggleMobile"
          >
            <span class="burger" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </button>
        </div>
      </div>
    </header>

    <!-- 必须放在 header 外：header 的 backdrop-filter 会把 fixed 菜单限制在顶栏高度内，被轮播盖住 -->
    <transition name="drawer-fade">
      <div v-if="mobileOpen" class="mobile-scrim" @click="closeMobile" />
    </transition>

    <transition name="drawer-slide">
      <aside v-if="mobileOpen" class="mobile-drawer" role="dialog" aria-modal="true" aria-label="站点菜单">
        <div class="drawer-head">
          <div class="drawer-brand">
            <span class="logo-mark sm" aria-hidden="true">
              <img v-if="showLogoImage" :src="site.logoUrl" alt="" class="logo-img">
              <span v-else class="logo-icon">{{ site.logoEmoji }}</span>
            </span>
            <div class="drawer-brand-text">
              <strong>{{ site.name }}</strong>
              <span v-if="site.logoSubtitle">{{ site.logoSubtitle }}</span>
            </div>
          </div>
          <button type="button" class="drawer-close" aria-label="关闭菜单" @click="closeMobile">
            <i class="el-icon-close" />
          </button>
        </div>

        <div class="drawer-search">
          <el-input
            v-model="keyword"
            placeholder="搜索文章、工具..."
            prefix-icon="el-icon-search"
            clearable
            @keyup.enter.native="handleSearchMobile"
          >
            <el-button slot="append" @click="handleSearchMobile">搜索</el-button>
          </el-input>
        </div>

        <nav class="drawer-nav">
          <p class="drawer-label">导航</p>
          <router-link
            v-for="(item, index) in navItems"
            :key="'m-' + index"
            :to="getNavTo(item)"
            class="drawer-link"
            :class="{ 'is-active': isNavActive(item) }"
            @click.native="closeMobile"
          >
            <span class="drawer-link-icon" :class="navIconClass(item)">
              <i :class="navIcon(item)" />
            </span>
            <span class="drawer-link-text">{{ item.label }}</span>
            <i class="el-icon-arrow-right drawer-link-arrow" />
          </router-link>
        </nav>

        <div class="drawer-foot">
          <p>滑动浏览 · 点按导航</p>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import { siteSettings } from '@/store/siteSettings'
import UserArea from '@/components/UserArea.vue'

export default {
  name: 'AppHeader',
  components: { UserArea },
  data() {
    return {
      isScrolled: false,
      keyword: '',
      mobileOpen: false
    }
  },
  computed: {
    site() {
      return siteSettings.config
    },
    navItems() {
      return siteSettings.navItems
    },
    showLogoImage() {
      return this.site.logoType === 'image' && this.site.logoUrl
    }
  },
  watch: {
    '$route'() {
      this.closeMobile()
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('keydown', this.onKeydown)
    this.unlockBody()
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.mobileOpen) this.closeMobile()
    },
    toggleMobile() {
      this.mobileOpen = !this.mobileOpen
      if (this.mobileOpen) this.lockBody()
      else this.unlockBody()
    },
    closeMobile() {
      if (!this.mobileOpen) return
      this.mobileOpen = false
      this.unlockBody()
    },
    lockBody() {
      document.documentElement.classList.add('nav-drawer-open')
    },
    unlockBody() {
      document.documentElement.classList.remove('nav-drawer-open')
    },
    getNavTo(item) {
      if (item.query) {
        return { path: item.path, query: item.query }
      }
      return item.path
    },
    isNavActive(item) {
      const { path, query } = this.$route

      if (item.path === '/') {
        return path === '/'
      }

      if (item.path === '/tools') {
        return path === '/tools' || path.startsWith('/tools/')
      }

      if (item.path === '/articles') {
        if (path !== '/articles') return false
        const currentType = query.type || ''
        const itemType = (item.query && item.query.type) || ''
        return currentType === itemType
      }

      return false
    },
    navIcon(item) {
      const type = (item.query && item.query.type) || ''
      const path = item.path || ''
      if (path === '/') return 'el-icon-s-home'
      if (path === '/tools' || path.startsWith('/tools')) return 'el-icon-s-tools'
      if (type === 'tech') return 'el-icon-cpu'
      if (type === 'software') return 'el-icon-monitor'
      if (type === 'tutorial') return 'el-icon-reading'
      if (type === 'resource') return 'el-icon-folder-opened'
      if (path === '/articles' && !type) return 'el-icon-document'
      return 'el-icon-menu'
    },
    navIconClass(item) {
      const type = (item.query && item.query.type) || ''
      const path = item.path || ''
      if (path === '/') return 'home'
      if (path === '/tools' || path.startsWith('/tools')) return 'tools'
      if (type) return type
      return 'default'
    },
    handleSearch() {
      const q = this.keyword.trim()
      if (!q) return
      this.$router.push({ path: '/articles', query: { q } })
    },
    handleSearchMobile() {
      this.handleSearch()
      this.closeMobile()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.app-header-shell {
  /* 不要加 transform / filter / backdrop-filter，否则会困住内部 fixed 菜单 */
  position: relative;
  z-index: 3000;
}

.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: calc(#{$header-height} + env(safe-area-inset-top, 0px));
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid transparent;
  z-index: 3001;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
  padding-top: env(safe-area-inset-top, 0);

  &.is-scrolled,
  &.menu-open {
    border-bottom-color: $border-color;
    box-shadow: $shadow-sm;
  }
}

.header-inner {
  height: $header-height;
  display: flex;
  align-items: center;
  gap: 24px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
  min-width: 0;

  &:hover {
    .logo-mark {
      transform: translateY(-1px);
      box-shadow: $shadow-md;
    }

    .logo-title {
      color: $primary-light;
    }
  }
}

.logo-mark {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, $primary-color 0%, $primary-light 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: $shadow-sm;
  transition: transform 0.25s, box-shadow 0.25s;
  overflow: hidden;
  flex-shrink: 0;

  &.sm {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-icon {
  font-size: 20px;
  line-height: 1;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.12));
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.2;
  min-width: 0;
}

.logo-title {
  font-size: 18px;
  font-weight: 800;
  color: $primary-color;
  letter-spacing: 0.04em;
  transition: color 0.2s;
}

.logo-sub {
  font-style: normal;
  font-size: 10px;
  font-weight: 500;
  color: $text-muted;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.nav-menu {
  display: flex;
  gap: 4px;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-item {
  padding: 8px 14px;
  border-radius: $radius-sm;
  color: $text-secondary;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: $primary-color;
    background: $primary-bg;
  }

  &.is-active {
    color: $primary-color;
    background: $primary-bg;
  }
}

.header-search {
  width: 180px;
  flex-shrink: 0;

  ::v-deep .el-input__inner {
    border-radius: 20px;
    border-color: $border-color;
    background: $bg-gray;

    &:focus {
      border-color: $primary-color;
    }
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}

.menu-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: $bg-gray;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.2s;

  &:active {
    background: $primary-bg;
  }

  &.open {
    background: $primary-bg;

    .burger i:nth-child(1) {
      transform: translateY(6px) rotate(45deg);
    }

    .burger i:nth-child(2) {
      opacity: 0;
      transform: scaleX(0);
    }

    .burger i:nth-child(3) {
      transform: translateY(-6px) rotate(-45deg);
    }
  }
}

.burger {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 18px;
  height: 14px;

  i {
    display: block;
    height: 2px;
    width: 100%;
    border-radius: 2px;
    background: $text-primary;
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s;
  }
}

.mobile-scrim {
  display: none;
}

.mobile-drawer {
  display: none;
}

@media (max-width: 992px) {
  .header-inner {
    gap: 12px;
  }

  .nav-item {
    padding: 6px 8px;
    font-size: 13px;
  }

  .header-search {
    width: 140px;
  }
}

@media (max-width: 768px) {
  .desktop-nav,
  .desktop-search {
    display: none;
  }

  .menu-toggle {
    display: inline-flex;
  }

  .logo-text {
    display: none;
  }

  .logo-mark {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }

  .header-inner {
    gap: 10px;
  }

  .mobile-scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 3010;
    background: rgba(15, 23, 42, 0.45);
  }

  .mobile-drawer {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 3020;
    width: min(86vw, 340px);
    padding: calc(12px + env(safe-area-inset-top, 0px)) 16px calc(16px + env(safe-area-inset-bottom, 0px));
    background:
      linear-gradient(180deg, #f8fbff 0%, #ffffff 28%),
      #fff;
    box-shadow: -12px 0 40px rgba(15, 23, 42, 0.18);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
  }

  .drawer-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .drawer-brand-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 15px;
      font-weight: 800;
      color: $primary-color;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    span {
      font-size: 11px;
      color: $text-muted;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .drawer-close {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: $bg-gray;
    color: $text-secondary;
    font-size: 16px;
    cursor: pointer;
    flex-shrink: 0;

    &:active {
      background: $border-color;
    }
  }

  .drawer-search {
    margin-bottom: 20px;

    ::v-deep .el-input-group {
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }

    ::v-deep .el-input__inner {
      border-radius: 14px 0 0 14px;
      background: #fff;
      height: 44px;
      line-height: 44px;
    }

    ::v-deep .el-input-group__append {
      background: $primary-color;
      border-color: $primary-color;
      color: #fff;
      padding: 0 14px;

      .el-button {
        color: #fff;
        border: none;
        background: transparent;
        padding: 0;
        font-weight: 600;
      }
    }
  }

  .drawer-label {
    font-size: 12px;
    font-weight: 600;
    color: $text-muted;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 10px;
    padding-left: 4px;
  }

  .drawer-nav {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
  }

  .drawer-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 12px;
    border-radius: 14px;
    color: $text-primary;
    font-size: 15px;
    font-weight: 600;
    background: transparent;
    border: 1px solid transparent;
    transition: background 0.2s, border-color 0.2s, color 0.2s;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: $bg-gray;
    }

    &.is-active {
      background: $primary-bg;
      border-color: $primary-border;
      color: $primary-color;

      .drawer-link-arrow {
        color: $primary-color;
        opacity: 1;
      }
    }
  }

  .drawer-link-icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;

    &.home {
      background: linear-gradient(135deg, #dbeafe, #bfdbfe);
      color: $primary-color;
    }

    &.tools {
      background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
      color: #6366f1;
    }

    &.tech {
      background: linear-gradient(135deg, #ecfdf5, #a7f3d0);
      color: #059669;
    }

    &.software {
      background: linear-gradient(135deg, #eff6ff, #bfdbfe);
      color: #2563eb;
    }

    &.tutorial {
      background: linear-gradient(135deg, #fff7ed, #fed7aa);
      color: #ea580c;
    }

    &.resource {
      background: linear-gradient(135deg, #fdf4ff, #e9d5ff);
      color: #9333ea;
    }

    &.default {
      background: $bg-gray;
      color: $text-secondary;
    }
  }

  .drawer-link-text {
    flex: 1;
    min-width: 0;
  }

  .drawer-link-arrow {
    font-size: 14px;
    color: $text-muted;
    opacity: 0.55;
  }

  .drawer-foot {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid $border-color;

    p {
      font-size: 12px;
      color: $text-muted;
      text-align: center;
    }
  }
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.25s ease;
}

.drawer-fade-enter,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(104%);
}
</style>
